import {
  useCompanies,
  useCreateCompany,
  useUpdateCompany,
} from '@/api/Companies';
import { useCompanyTypes } from '@/api/CompanyTypes';
import { useCountries } from '@/api/Countries';
import { Company } from '@/types';
import { Button, Switch } from 'antd';
import React, { useMemo, useState } from 'react';
import { CustomColumns, CustomTable, faEdit, Icon } from '@/ui/components';
import { useAppCustomers } from '@/hooks';
import { useTranslate } from '@/translations';
import { CompanyPopup } from '../CompanyPopup';

export const CompaniesDataGrid: React.FC = () => {
  const { t } = useTranslate();
  const {
    userPermissions: { customers },
    hasGbaAdminAccess,
  } = useAppCustomers();

  const {
    data: companiesData,
    isFetching: isCompaniesLoading,
    refetch,
  } = useCompanies();
  const { mutateAsync: createMutate } = useCreateCompany();
  const { mutateAsync: updateMutate } = useUpdateCompany();

  const { data: countries, isFetching: isCountriesLoading } = useCountries();
  const { data: companyTypes, isFetching: isCompanyTypesLoading } =
    useCompanyTypes();

  const [company, setCcompany] = useState<Company>();

  const onSubmit = async (data: Company) => {
    const res = await (data.companyId
      ? updateMutate(data)
      : createMutate(data));

    if (res.success) refetch();

    return res.message?.Message;
  };

  const isLoading =
    isCompaniesLoading || isCountriesLoading || isCompanyTypesLoading;

  const columns: CustomColumns<Company> = useMemo(
    () => [
      {
        dataIndex: 'customerId',
        title: 'Customer',
        render: (_, data) => data.customerName,
        renderType: 'select',
        filterOptions: (customers || []).map((c) => ({
          text: c.customerId,
          value: c.customerName,
        })),
        filter: true,
        sortable: true,
      },
      {
        dataIndex: 'companyName',
        title: 'Company Name',
        filter: true,
        sortable: true,
      },
      {
        dataIndex: 'companyCode',
        title: 'Company Code',
        width: 200,
        filter: true,
        sortable: true,
      },
      {
        dataIndex: 'countryId',
        title: 'Country Description',
        render: (_, data) => data.country,
        width: 250,
        renderType: 'select',
        filterOptions: (countries || []).map((c) => ({
          text: c.name,
          value: c.id,
        })),
        filter: true,
        sortable: true,
      },
      {
        dataIndex: 'companyTypeId',
        title: 'Company Type',
        render: (_, data) => data.companyType,
        width: 200,
        renderType: 'select',
        filterOptions: (companyTypes || []).map((c) => ({
          text: c.name,
          value: c.id,
        })),
        filter: true,
        sortable: true,
      },
      {
        dataIndex: 'isActive',
        title: 'Active',
        width: 100,
        render: (value) => <Switch checked={value} />,
        align: 'center',
      },
      {
        dataIndex: 'companyId',
        render: (_, data) =>
          hasGbaAdminAccess ? (
            <Button onClick={() => setCcompany({ ...data })}>
              <Icon icon={faEdit} /> {t('edit')}
            </Button>
          ) : null,
        width: 150,
      },
    ],
    [companyTypes, countries, customers, hasGbaAdminAccess, t],
  );

  return (
    <>
      <CustomTable
        dataSource={companiesData || []}
        columns={columns}
        rowKey="companyId"
        loading={isLoading}
        onAdd={() => setCcompany({} as Company)}
      />

      <CompanyPopup
        data={company}
        countries={countries || []}
        companyTypes={companyTypes || []}
        customers={
          customers?.map((o) => ({
            label: o.customerName,
            value: o.customerId,
          })) || []
        }
        onCancel={() => setCcompany(undefined)}
        onSubmit={onSubmit}
      />
    </>
  );
};
